import { useTranslation } from '@dukanti/shared';
import type { StockWorklistCounts, StockWorklistScope } from '@dukanti/shared/admin';
import { Skeleton } from '@/components/ui/skeleton';

export interface StockSummaryHeaderProps {
  readonly counts: StockWorklistCounts | null;
  readonly scope: StockWorklistScope;
  readonly isLoading: boolean;
}

export function StockSummaryHeader({
  counts,
  scope,
  isLoading,
}: StockSummaryHeaderProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const title = t('admin.stock.worklist.scopeSummary', {
    scope: t(
      scope === 'active' ? 'admin.stock.worklist.scopeActive' : 'admin.stock.worklist.scopeAll',
    ),
  });
  const cards = [
    {
      key: 'variants',
      label: t('admin.stock.worklist.variants'),
      value: counts?.variants,
      color: 'text-foreground',
    },
    {
      key: 'outOfStock',
      label: t('admin.stock.worklist.outCount'),
      value: counts?.outOfStock,
      color: 'text-destructive',
    },
    {
      key: 'lowStock',
      label: t('admin.stock.worklist.lowCount'),
      value: counts?.lowStock,
      color: 'text-amber-700 dark:text-amber-200',
    },
  ];
  return (
    <section dir={dir} aria-label={title} data-testid="stock-summary-header" className="space-y-2">
      <h2 className="text-foreground text-sm font-semibold">{title}</h2>
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {cards.map(({ key, label, value, color }) => (
          <div
            key={key}
            data-testid={`stock-summary-${key}`}
            className="border-border bg-card shadow-admin-card min-w-0 rounded-xl border p-3 sm:p-4"
          >
            <p className="text-muted-foreground text-xs sm:text-sm">{label}</p>
            {isLoading ? (
              <Skeleton className="mt-2 h-8 w-12" />
            ) : (
              <p className={`mt-1 text-2xl font-semibold tabular-nums sm:text-3xl ${color}`}>
                {value === undefined ? '—' : new Intl.NumberFormat(lang).format(value)}
              </p>
            )}
          </div>
        ))}
      </div>
      <p className="text-muted-foreground text-xs">{t('admin.stock.worklist.scopeHelp')}</p>
    </section>
  );
}
